import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, LoadingSkeleton, Row } from "@narmi/design_system";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { type Control, useForm } from "react-hook-form";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Card } from "@lending/components/Card/Card";
import { Combobox } from "@lending/components/ComboBox/Combobox";
import { CommaFormattedNumberInput } from "@lending/components/CommaFormattedNumberInput";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { TextInput } from "@lending/components/TextInput/TextInput";
import {
  AutoDetailsFormData,
  AutoDetailsFormInput,
  createSchema,
} from "@lending/hooks/queries/AutoLoanApplication/types";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useVehicleOptionsQuery } from "@lending/hooks/queries/useVehicleOptionsQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { usePatchVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/usePatchVehicleLoanApplicationQuery";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./AutoDetails.module.css";
import { type DecodedVehicleDetails, useVinLookup } from "./components/VinCollection/useVinLookup";
import { VinDetailsPrompt } from "./components/VinCollection/VinDetailsPrompt";
import { VinLookupPanel } from "./components/VinCollection/VinLookupPanel";
import { NewUsedSelect } from "../../../components/NewUsedSelect/NewUsedSelect";

// Type alias for the vehicle options response structure
type VehicleOptions = Record<string, Record<string, string[]>>;

function AutoDetails() {
  const { l10n } = useLocalization();
  const { goBack, goNext, startOver } = useFlowNavigation();
  const { showGenericErrorToast, showErrorToast } = useToastsContext();
  // Get application ID from store
  const lendingApplicationID = useLendingApplicationID();
  const queryClient = useQueryClient();
  const patchVehicleLoanApplicationQuery = usePatchVehicleLoanApplicationQuery();
  const { data: application } = useGetVehicleLoanApplicationQuery();
  const isRefinance = application?.is_refinance === true;
  const { institution } = useGetInstitutionQuery();
  const maxVehicleAgeYears = institution?.lending_maximum_vehicle_age_years ?? 0;
  const collectVin =
    institution?.lending_ask_additional_fields?.some(
      (field) => field.name === "collect_vin" && field.value
    ) ?? false;
  const schema = useMemo(
    () => createSchema(l10n, isRefinance, maxVehicleAgeYears),
    [l10n, isRefinance, maxVehicleAgeYears]
  );

  // Animation state
  const [showConditionalFields, setShowConditionalFields] = useState(false);

  const { vehicleOptions: rawVehicleOptions, isLoading: isVehicleOptionsLoading } =
    useVehicleOptionsQuery();
  // Cast the raw data to our expected shape if present. If the backend returns an unexpected
  // shape, the optional chaining in downstream usage prevents runtime errors.
  const vehicleOptions = rawVehicleOptions as VehicleOptions | undefined;

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { isSubmitting },
  } = useForm<AutoDetailsFormInput, unknown, AutoDetailsFormData>({
    defaultValues: {
      condition: "new",
      vin: "",
      year: undefined,
      make: "",
      model: "",
      trim: "",
      mileage: undefined,
    },
    resolver: zodResolver(schema),
  });

  // Child field components (TextInput, Combobox, etc.) accept the 2-generic
  // `Control<Input>` form. Because the zod schema transforms `year` on submit,
  // `useForm` here emits a 3-generic control whose output shape doesn't match.
  // Narrow it once so the JSX below stays clean.
  const formControl = control as unknown as Control<AutoDetailsFormInput>;

  const hasSeededFromApplication = useRef(false);
  const previousMakeValue = useRef<string | undefined>(undefined);
  const previousModelValue = useRef<string | undefined>(undefined);
  const skipNextDependentClear = useRef(false);

  // Use patch mutation loading state for submit button
  const isLoading = isSubmitting || patchVehicleLoanApplicationQuery.isPending;

  // Watch for year and make values and trigger animation
  const condition = watch("condition");
  const effectiveCondition = isRefinance ? "used" : condition;
  const yearValue = watch("year");
  const makeValue = watch("make");
  const modelValue = watch("model");
  const vinValue = watch("vin") ?? "";
  const isShowingConditionalFields = Boolean(watch("year") && watch("make"));
  const heading = isRefinance
    ? l10n.getString("auto-loan-auto-details-refinance-heading", null, "Tell us about your car")
    : l10n.getString("auto-loan-auto-details-heading", null, "Tell us about your new car");
  const subheading = isRefinance
    ? l10n.getString(
        "auto-loan-auto-details-refinance-subheading",
        null,
        "We need a few vehicle details to get you the most accurate loan offer."
      )
    : l10n.getString(
        "auto-loan-auto-details-subheading",
        null,
        "We need these details to prepare an accurate loan offer."
      );

  // Get available makes, models, and trims based on vehicleOptions and selections
  // and transform vehicle options into dropdown-compatible format for makes
  const availableMakes = useMemo(() => {
    if (!vehicleOptions) return [];
    return Object.keys(vehicleOptions).map((make) => ({ code: make, name: make }));
  }, [vehicleOptions]);

  // Filter available models based on the selected make
  const availableModels = useMemo(() => {
    const modelsForMake = vehicleOptions?.[makeValue];
    if (!modelsForMake) return [];
    return Object.keys(modelsForMake).map((model) => ({ code: model, name: model }));
  }, [vehicleOptions, makeValue]);

  // Filter available trims based on the selected make and model
  const availableTrims = useMemo(() => {
    const trims = vehicleOptions?.[makeValue]?.[modelValue];
    if (!Array.isArray(trims)) return [];
    return trims.map((trim) => ({ code: trim, name: trim }));
  }, [vehicleOptions, makeValue, modelValue]);

  // Applies a decode result to the form. `useVinLookup` invokes this once per
  // decoded VIN, so prefill never overwrites the applicant's later edits.
  function applyDecodedVehicle(decoded: DecodedVehicleDetails) {
    skipNextDependentClear.current = true;
    if (decoded.year) {
      setValue("year", decoded.year, { shouldValidate: true });
    }
    if (decoded.make) {
      // NHTSA reports makes uppercase (e.g. "HONDA"); prefer the canonical
      // casing from the vehicle options dropdown when one matches. The
      // Combobox accepts free-form values, so unmatched decodes still apply.
      const matchedMake = availableMakes.find(
        (make) => make.code.toLowerCase() === decoded.make?.toLowerCase()
      )?.code;
      setValue("make", matchedMake ?? decoded.make, { shouldValidate: true });
      setValue("model", decoded.model ?? "", { shouldValidate: true });
      setValue("trim", decoded.trim ?? "", { shouldValidate: true });
    }
  }

  const { status: vinLookupStatus, markApplied: markVinApplied } = useVinLookup({
    vin: vinValue,
    enabled: collectVin,
    onDecoded: applyDecodedVehicle,
  });

  useEffect(() => {
    // Waits for the institution so `collectVin` is settled before seeding:
    // when VIN collection is off, a saved VIN must stay out of the form —
    // it isn't rendered, isn't submitted, and legacy values that predate the
    // stricter VIN validation must not block submission.
    if (hasSeededFromApplication.current || !application || !institution) return;
    const seededVin = collectVin ? (application.vin ?? "") : "";
    const hasSavedVehicleDetails =
      application.vehicle_year != null ||
      application.vehicle_make != null ||
      application.vehicle_model != null ||
      application.vehicle_trim != null ||
      application.vehicle_mileage != null ||
      seededVin !== "";
    if (!hasSavedVehicleDetails) return;
    skipNextDependentClear.current = true;
    // A saved VIN's details are already on the application; mark it applied
    // so it doesn't re-decode and overwrite edits the applicant made later.
    markVinApplied(seededVin === "" ? null : seededVin);
    // `AutoDetailsFormInput` is the zod *input* type (year: string as entered in the text input).
    reset({
      condition: application.vehicle_condition === "used" ? "used" : "new",
      vin: seededVin,
      year: application.vehicle_year != null ? String(application.vehicle_year) : undefined,
      make: application.vehicle_make ?? "",
      model: application.vehicle_model ?? "",
      trim: application.vehicle_trim ?? "",
      mileage: application.vehicle_mileage ?? undefined,
    });
    hasSeededFromApplication.current = true;
  }, [application, institution, collectVin, reset, markVinApplied]);

  useEffect(() => {
    if (previousMakeValue.current !== undefined && previousMakeValue.current !== makeValue) {
      if (skipNextDependentClear.current) {
        previousMakeValue.current = makeValue;
        return;
      }
      setValue("model", "");
      setValue("trim", "");
    }
    previousMakeValue.current = makeValue;
  }, [makeValue, setValue]);

  useEffect(() => {
    if (previousModelValue.current !== undefined && previousModelValue.current !== modelValue) {
      if (skipNextDependentClear.current) {
        previousModelValue.current = modelValue;
        skipNextDependentClear.current = false;
        return;
      }
      setValue("trim", "");
    }
    previousModelValue.current = modelValue;
  }, [modelValue, setValue]);

  // Manages the visibility animation for model, trim, and mileage fields
  useEffect(() => {
    const shouldShow = Boolean(yearValue && makeValue);
    if (shouldShow !== showConditionalFields) {
      if (shouldShow) {
        // Small delay so the DOM can update before starting animation
        const timer = setTimeout(() => setShowConditionalFields(true), 50);
        return () => clearTimeout(timer);
      } else {
        setShowConditionalFields(false);
      }
    }
  }, [yearValue, makeValue, showConditionalFields]);

  async function onSubmit(data: AutoDetailsFormData) {
    if (!lendingApplicationID) {
      showGenericErrorToast();
      startOver();
      return;
    }

    try {
      const resolvedCondition = isRefinance ? "used" : data.condition;

      await patchVehicleLoanApplicationQuery.mutateAsync({
        params: {
          path: {
            uuid: lendingApplicationID,
          },
        },
        body: {
          has_vehicle_selected: true,
          vehicle_condition: resolvedCondition,
          vehicle_year: data.year,
          vehicle_make: data.make,
          vehicle_model: data.model,
          vehicle_trim: data.trim,
          vehicle_mileage: resolvedCondition === "used" ? data.mileage : null,
          // An empty VIN input clears any previously saved VIN.
          ...(collectVin ? { vin: data.vin?.length ? data.vin : null } : {}),
        },
      });

      // Invalidate queries to refetch updated application
      await queryClient.invalidateQueries({
        queryKey: [
          "get",
          "/v2/lending/vehicle_loan_applications/{uuid}/",
          { params: { path: { uuid: lendingApplicationID } } },
        ],
      });

      // Navigate to the next step in the auto loan flow
      goNext();
    } catch (error) {
      console.error("Error saving auto details:", error);
      showErrorToast(
        l10n.getString(
          "error-vehicle-details-save",
          null,
          "There was an error saving your vehicle details. Please try again."
        )
      );
    }
  }

  return (
    <PageLayout>
      <div className={styles.autoDetailsContainer}>
        <div className={styles.content}>
          <div>
            <PageHeader heading={heading} subheading={subheading} />
          </div>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.formContainer}>
            <Card className={styles.mainCard}>
              <LoadingSkeleton
                isLoading={isVehicleOptionsLoading}
                content="paragraph"
                lines={isRefinance ? 2 : 3}
                testId="auto-details-loading-skeleton"
              >
                {!isRefinance && (
                  <>
                    <NewUsedSelect control={formControl} />
                    {!collectVin && <div className="margin--top--xl" />}
                  </>
                )}
                {collectVin && (
                  <VinLookupPanel
                    control={formControl}
                    vinLength={vinValue.length}
                    status={vinLookupStatus}
                    hasPrecedingToggle={!isRefinance}
                  />
                )}
                {collectVin && <VinDetailsPrompt showReview={vinLookupStatus === "matched"} />}
                <TextInput
                  name="year"
                  control={formControl}
                  label={l10n.getString("auto-loan-auto-details-year-label", null, "Year")}
                  type="text"
                  inputMode="numeric"
                  placeholder={l10n.getString("placeholder-year", null, "Year")}
                  transform={(value) => value.replace(/\D/g, "").slice(0, 4)}
                />
                <div className="margin--top--m" />
                <Combobox
                  name="make"
                  control={formControl}
                  label={l10n.getString("auto-loan-auto-details-make-label", null, "Make")}
                  items={availableMakes}
                />
                {isShowingConditionalFields && (
                  <>
                    <div className="margin--top--m" />
                    <div
                      className={`${styles.conditionalFields} ${showConditionalFields ? styles.conditionalFieldsVisible : ""}`}
                    >
                      <div
                        className={`${styles.modelTrimRow} ${showConditionalFields ? styles.modelTrimRowVisible : ""}`}
                      >
                        <Combobox
                          name="model"
                          control={formControl}
                          label={l10n.getString(
                            "auto-loan-auto-details-model-label",
                            null,
                            "Model"
                          )}
                          items={availableModels}
                        />
                        <Combobox
                          name="trim"
                          control={formControl}
                          label={l10n.getString(
                            "auto-loan-auto-details-trim-label",
                            null,
                            "Trim (optional)"
                          )}
                          items={availableTrims}
                        />
                      </div>
                      {effectiveCondition === "used" && (
                        <div
                          className={`${styles.mileageField} ${showConditionalFields ? styles.mileageFieldVisible : ""}`}
                        >
                          <CommaFormattedNumberInput
                            name="mileage"
                            control={formControl}
                            label={l10n.getString(
                              "auto-loan-auto-details-mileage-label",
                              null,
                              "Mileage"
                            )}
                            placeholder={l10n.getString("placeholder-mileage", null, "Mileage")}
                            tooltipText={l10n.getString(
                              "auto-loan-auto-details-mileage-tooltip",
                              null,
                              "An estimate is fine. We'll verify the exact mileage later."
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </LoadingSkeleton>
            </Card>
            <div className="margin--top--xl">
              <Row alignItems="center" justifyContent="space-between">
                <BackButton onClick={goBack} />
                <Button
                  label={l10n.getString("button-label-next", null, "Next")}
                  type="submit"
                  isLoading={isLoading}
                />
              </Row>
            </div>
          </form>
        </div>
      </div>
    </PageLayout>
  );
}

export { AutoDetails };
